/*
 * Truth and Reconciliation Day gift (Sep 30 and Oct 1 2026).
 * Config: window.ATOME_TRC from snippets/trc-promo.liquid (the one config spot).
 *
 * Inside the window, every cart with shippable food gets exactly one ONE-TIME
 * line of the gift variant with properties._trc_gift = "1" (plus one visible
 * property). Never a selling_plan, so it stays a plain line next to a Recharge
 * box, like the pan and extras lines. The Admin automatic BXGY discount makes
 * it $0; the theme only adds, dedupes, and removes the line.
 *
 * - ensure() is idempotent and never rejects. It is awaited before every
 *   checkout: cart page / drawer checkout forms, /checkout links, and the
 *   direct-to-checkout flows in bmb-builder.js, gift-box-cart.js, pan-page.js,
 *   bab-custom.js (they call window.AtomeTRC.ensure() before navigating).
 * - Time: server Date header of /cart.js, client clock as fallback.
 * - Customer removed the gift line: remembered per cart token in
 *   sessionStorage, never re-added for that cart.
 * - Outside the window (or kill switch off): any _trc_gift line is removed.
 * - Pure decision logic (core) is exported for node: ops/trc-gift.test.mjs.
 */
(function (root) {
  'use strict';

  var core = (function () {
    function toMs(iso) {
      var t = Date.parse(iso);
      return isNaN(t) ? null : t;
    }

    // [start, end): at START the gift is on, at END it is off.
    function isInWindow(nowMs, cfg) {
      if (!cfg || !cfg.enabled) return false;
      var s = toMs(cfg.start);
      var e = toMs(cfg.end);
      if (s == null || e == null) return false;
      return nowMs >= s && nowMs < e;
    }

    function isActive(nowMs, cfg, forceOn) {
      if (!cfg || !cfg.enabled) return false;
      return !!forceOn || isInWindow(nowMs, cfg);
    }

    function isTrcLine(item) {
      return !!(item && item.properties && item.properties._trc_gift);
    }

    function isGiftNoteLine(item) {
      return item.handle === 'gift-note' || String(item.sku || '').toUpperCase() === 'GIFT-NOTE';
    }

    // Shippable food (or pan). Gift cards, non-shipping lines, the gift note,
    // and the gift product itself (even paid units) do not earn a gift. Keep
    // in step with customerBuys in ops/trc-discount.graphql, else the line
    // would be added but charged.
    function isQualifyingLine(item, cfg) {
      if (!item || isTrcLine(item)) return false;
      if (cfg && Number(item.variant_id) === Number(cfg.variantId)) return false;
      if (item.gift_card) return false;
      if (item.requires_shipping === false) return false;
      if (isGiftNoteLine(item)) return false;
      return true;
    }

    function buildAddItem(cfg) {
      var props = { _trc_gift: '1' };
      if (cfg.lineLabel && cfg.lineNote) props[cfg.lineLabel] = cfg.lineNote;
      return { id: Number(cfg.variantId), quantity: 1, properties: props };
    }

    function noop(reason) {
      return { action: 'noop', reason: reason, ops: [] };
    }

    function removeAll(lines, reason) {
      return {
        action: 'remove',
        reason: reason,
        ops: lines.map(function (l) { return { type: 'change', key: l.key, quantity: 0 }; })
      };
    }

    // input: { cart, cfg, nowMs, forceOn, declined }
    // returns { action: 'add'|'remove'|'fix'|'noop', reason, ops: [...] }
    function decide(input) {
      var cfg = input.cfg || {};
      var items = (input.cart && input.cart.items) || [];
      var trcLines = items.filter(isTrcLine);

      if (!isActive(input.nowMs, cfg, input.forceOn)) {
        return trcLines.length ? removeAll(trcLines, 'outside_window') : noop('outside_window');
      }
      if (!items.some(function (i) { return isQualifyingLine(i, cfg); })) {
        return trcLines.length ? removeAll(trcLines, 'no_qualifying_items') : noop('no_qualifying_items');
      }
      if (!trcLines.length) {
        if (input.declined) return noop('declined');
        return { action: 'add', reason: 'missing', ops: [{ type: 'add', item: buildAddItem(cfg) }] };
      }
      var ops = [];
      if (trcLines[0].quantity !== 1) ops.push({ type: 'change', key: trcLines[0].key, quantity: 1 });
      trcLines.slice(1).forEach(function (l) { ops.push({ type: 'change', key: l.key, quantity: 0 }); });
      return ops.length ? { action: 'fix', reason: 'duplicate_or_qty', ops: ops } : noop('present');
    }

    return {
      isInWindow: isInWindow,
      isActive: isActive,
      isTrcLine: isTrcLine,
      isQualifyingLine: isQualifyingLine,
      buildAddItem: buildAddItem,
      decide: decide
    };
  })();

  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  if (!root || root.AtomeTRC || !root.ATOME_TRC) return;

  var CFG = root.ATOME_TRC;
  var DECLINED_KEY = 'trc_gift_declined';
  var PREVIEW_KEY = 'trc_preview';
  var RELOAD_KEY = 'trc_cart_reload_at';
  var DAY = 86400000;
  var clockOffsetMs = 0;
  var lastToken = null;

  function previewOn() {
    try {
      var role = root.Shopify && root.Shopify.theme && root.Shopify.theme.role;
      if (!role || role === 'main') return false;
      return sessionStorage.getItem(PREVIEW_KEY) === '1';
    } catch (e) { return false; }
  }

  function now() { return Date.now() + clockOffsetMs; }

  // Only do network work around the promo, so the rest of the year no page
  // pays for a /cart.js call. Wide enough to clean up stale lines after END.
  function nearWindow() {
    if (previewOn()) return true;
    var s = Date.parse(CFG.start);
    var e = Date.parse(CFG.end);
    var t = now();
    return !isNaN(s) && !isNaN(e) && t >= s - 2 * DAY && t < e + 14 * DAY;
  }

  // Wider tail than nearWindow(): a cart can sit untouched for weeks holding
  // a stale _trc_gift line at full price. Checkout interceptors and
  // AtomeTRC.ensure() use this so that line is cleaned up before checkout
  // long after the page-load window has closed.
  function nearCheckoutCleanup() {
    if (previewOn()) return true;
    var s = Date.parse(CFG.start);
    var e = Date.parse(CFG.end);
    var t = now();
    return !isNaN(s) && !isNaN(e) && t >= s - 2 * DAY && t < e + 60 * DAY;
  }

  function isCartPage() {
    return !!(root.themeVariables && root.themeVariables.settings && root.themeVariables.settings.pageType === 'cart');
  }

  function syncUi() {
    var on = core.isActive(now(), CFG, previewOn());
    document.documentElement.classList.toggle('trc-active', on);
  }

  function readDeclined(token) {
    try {
      var raw = sessionStorage.getItem(DECLINED_KEY);
      if (!raw) return false;
      var d = JSON.parse(raw);
      if (d.token == null && token) {
        d.token = token;
        sessionStorage.setItem(DECLINED_KEY, JSON.stringify(d));
      }
      return d.token === token;
    } catch (e) { return false; }
  }

  function markDeclined() {
    try { sessionStorage.setItem(DECLINED_KEY, JSON.stringify({ token: lastToken, at: Date.now() })); } catch (e) {}
  }

  var FETCH_TIMEOUT_MS = 4000;

  // So a hung /cart.js or /cart/*.js request cannot block the ensure() queue.
  function withTimeout(fn) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    return fn(controller ? controller.signal : undefined).finally(function () {
      if (timer) clearTimeout(timer);
    });
  }

  function fetchCart() {
    return withTimeout(function (signal) {
      return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: signal }).then(function (res) {
        var d = Date.parse(res.headers.get('Date') || '');
        if (!isNaN(d)) clockOffsetMs = d - Date.now();
        if (!res.ok) throw new Error('cart.js ' + res.status);
        return res.json();
      });
    });
  }

  function post(url, body) {
    return withTimeout(function (signal) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: signal
      });
    });
  }

  function applyOps(ops) {
    var changed = false;
    return ops.reduce(function (p, op) {
      return p.then(function () {
        var req = op.type === 'add'
          ? post('/cart/add.js', { items: [op.item] })
          : post('/cart/change.js', { id: op.key, quantity: op.quantity });
        return req.then(function (res) { if (res.ok) changed = true; }, function () {});
      });
    }, Promise.resolve()).then(function () { return changed; });
  }

  // Tell the theme (drawer, count bubble, free-shipping bar) the cart changed.
  function announce() {
    if (isCartPage()) {
      try {
        var last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last < 10000) return;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch (e) {}
      location.reload();
      return;
    }
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
    post('/cart/update.js', { sections: sections }).then(function (res) { return res.ok ? res.json() : null; }).then(function (cart) {
      if (!cart) return;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'trc-gift', cart: cart } }));
    }).catch(function () {});
  }

  function runOnce(opts) {
    return fetchCart().then(function (cart) {
      lastToken = cart.token || null;
      syncUi();
      var decision = core.decide({
        cart: cart,
        cfg: CFG,
        nowMs: now(),
        forceOn: previewOn(),
        declined: readDeclined(lastToken)
      });
      return applyOps(decision.ops).then(function (changed) {
        if (changed && !(opts && opts.navigating)) announce();
        return { decision: decision, changed: changed };
      });
    });
  }

  var queue = Promise.resolve();
  function ensure(opts) {
    var run = queue.then(function () { return runOnce(opts); });
    queue = run.catch(function () {});
    var timeoutMs = (opts && opts.timeoutMs) || 6000;
    var timer = new Promise(function (resolve) { setTimeout(function () { resolve({ timeout: true }); }, timeoutMs); });
    return Promise.race([run.catch(function (e) { return { error: String(e) }; }), timer]).then(function (r) {
      document.documentElement.classList.add('trc-ready');
      return r;
    });
  }

  // Direct-to-checkout flows: await AtomeTRC.ensure() before navigating.
  root.AtomeTRC = {
    core: core,
    ensure: function () { return nearCheckoutCleanup() ? ensure({ navigating: true, timeoutMs: 5000 }) : Promise.resolve({ skipped: true }); }
  };

  /* ---- Checkout interception ---- */
  function isCheckoutHref(href) {
    try {
      var u = new URL(href, location.href);
      return u.origin === location.origin && /^\/checkout(\/|$)/.test(u.pathname);
    } catch (e) { return false; }
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !isCheckoutHref(a.href) || !nearCheckoutCleanup()) return;
    e.preventDefault();
    var href = a.href;
    root.AtomeTRC.ensure().then(function () { location.href = href; });
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || form.__trcPassed || !nearCheckoutCleanup()) return;
    var action = form.getAttribute('action') || '';
    var submitter = e.submitter || null;
    var toCheckout = /\/checkout/.test(action) ||
      (/\/cart\/?(\?|$)/.test(action) && ((submitter && submitter.name === 'checkout') || (!submitter && form.querySelector('[name="checkout"]'))));
    if (!toCheckout) return;
    e.preventDefault();
    if (form.__trcPending) return;
    form.__trcPending = true;
    if (submitter) submitter.setAttribute('aria-busy', 'true');
    root.AtomeTRC.ensure().then(function () {
      form.__trcPassed = true;
      if (submitter && submitter.name && !form.querySelector('input[type="hidden"][name="' + submitter.name + '"]')) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = submitter.name;
        hidden.value = submitter.value || '';
        form.appendChild(hidden);
      }
      HTMLFormElement.prototype.submit.call(form);
    });
  }, true);

  /* ---- Customer removed the gift line: respect it for this cart ---- */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('[data-trc-gift-line] a[href*="/cart/change"]') : null;
    if (a) markDeclined();
  }, true);

  /* ---- Keep the cart right as it changes ---- */
  var debounce = null;
  document.addEventListener('cart:change', function (e) {
    if (e.detail && e.detail.baseEvent === 'trc-gift') return;
    if (!nearWindow()) return;
    clearTimeout(debounce);
    debounce = setTimeout(function () { ensure(); }, 300);
  });

  // Page load: sync server time + banner, add/clean the line. The cart page
  // always checks so a stale line after END is removed there.
  function onReady() {
    if (isCartPage() || nearWindow()) ensure();
    else document.documentElement.classList.add('trc-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})(typeof window !== 'undefined' ? window : null);
